<template>
	<section>
		<title>Dashboard | Member Welcomer</title>
		<div class="page-loader" v-if="!loaded">
			<div class="page-pulse">
				<div></div>
				<div></div>
				<div></div>
			</div>
		</div>
		<div class="state-bar" v-if="loaded">
			<a href="/dashboard">Go To The Server Selection Menu</a>
		</div>
		<form class="form" v-if="loaded">
			<div class="form-field">
				<p class="form-field-title">Join Message Channel</p>
				<p class="form-field-title-description">The channel where the welcome message will be sent in.</p>
				<v-select :options="textChannels.map(channel => ({ label: `# ${channel.name}`, value: channel.id }))"
					@input="update('join-opt')" v-model="joinOpt"></v-select>
			</div>
			<div class="form-field">
				<p class="form-field-title">Join Message</p>
				<p class="form-field-title-description">Message that will be sent automatically to Join Message Channel
					upon someone's arrival.</p>
				<textarea class="form-field-textarea" @input="update('join-msg')" v-model="joinMessage"
					placeholder="Welcome {member.mention}!"></textarea>
			</div>
			<div class="form-field">
				<p class="form-field-title">Join Direct Message</p>
				<p class="form-field-title-description">Message that will be sent to user in direct messages upon
					arrival. Leave empty for disabled.</p>
				<textarea class="form-field-textarea" @input="update('join-private')" v-model="joinPrivate"
					placeholder="Welcome to {server.name}, you are {server.memberCount}th member."></textarea>
			</div>
			<div class="form-field">
				<p class="form-field-title">Leave Message Channel</p>
				<p class="form-field-title-description">The channel where the leave message will be sent in.</p>
				<v-select :options="textChannels.map(channel => ({ label: `# ${channel.name}`, value: channel.id }))"
					@input="update('leave-opt')" v-model="leaveOpt"></v-select>
			</div>
			<div class="form-field">
				<p class="form-field-title">Leave Message</p>
				<p class="form-field-title-description">Message that will be sent automatically to Leave Message Channel
					upon someone's departure.</p>
				<textarea class="form-field-textarea" @input="update('leave-msg')" v-model="leaveMessage"
					placeholder="Welcome to {server.name}, you are {server.memberCount}th member."></textarea>
			</div>
			<div class="form-field">
				<p class="form-field-title">~ Rich Syntax ~</p>
				<p class="form-field-title-description">You can use rich syntax literals in all of the Welcomer's
					message contents.</p>
				<textarea class="form-field-textarea" style="min-height: 90px!important;max-height: 90px!important;"
					v-model="richSyntax" readonly></textarea>
			</div>
		</form>
	</section>
</template>

<script>
export default {
	name: "Welcomer",
	watch: {
		confirm(value) {
			this.$emit("setPromptVisibility", value);
		},
		saving(value) {
			this.$emit("setPromptLoading", value);
		}
	},
	data() {
		return {
			loaded: false,
			settings: {},
			cache: {},
			changes: {},
			confirm: false,
			textChannels: null,
			joinOpt: null,
			join: null,
			joinMessage: null,
			joinPrivate: null,
			leaveOpt: null,
			leave: null,
			leaveMessage: null,
			saving: false,
			richSyntax: `{member.username} - Member's that joined/left username.
{server.memberCount} - The server member joined/left member count.
{server.name} - The server member joined/left name.
{member.mention} - The @mention of the member joined. (Not available for leave message.)`
		};
	},
	methods: {
		fetchGuildData: async function () {
			const response = await this.$axios.get(`${this.$apiBase}/v1/information/${this.$route.params.id}/cache`, {
				withCredentials: true,
				credentials: "same-origin"
			});
			this.cache = response.data.data;

			const settingsResponse = await this.$axios.get(`${this.$apiBase}/v1/information/${this.$route.params.id}/settings`, {
				withCredentials: true,
				credentials: "same-origin"
			});

			this.settings = settingsResponse.data.data;
			this.loaded = true;

			this.init();
		},
		init: function () {
			const GuildTextType = 0;
			this.textChannels = this.arraify(this.cache.channels).filter(c => c.type === GuildTextType);
			/* Join Channel */
			if (this.settings.join_message_channel) {
				let joinChannel = this.textChannels.find(c => c.id === this.settings.join_message_channel);
				this.joinOpt = {
					label: joinChannel ? "# " + joinChannel.name : "# deleted-channel",
					value: this.settings.join_message_channel
				};
			} else {
				this.joinOpt = {
					label: "None",
					value: null
				};
			}
			this.join = this.settings.join_message_channel;
			/* Join Message */
			this.joinMessage = this.settings.join_message_content;
			/* Private Message */
			this.joinPrivate = this.settings.join_private_message;
			/* Leave Channel */
			if (this.settings.leave_message_channel) {
				let leaveChannel = this.textChannels.find(c => c.id === this.settings.leave_message_channel);
				this.leaveOpt = {
					label: leaveChannel ? "# " + leaveChannel.name : "# deleted-channel",
					value: this.settings.leave_message_channel
				};
			} else {
				this.leaveOpt = {
					label: "None",
					value: null
				};
			}
			this.leave = this.settings.leave_message_channel;
			/* Leave Message */
			this.leaveMessage = this.settings.leave_message_content;
		},
		arraify: function (object) {
			let output = [];
			for (const key of Object.keys(object)) {
				output.push(object[key]);
			}
			return output;
		},
		save: async function () {
			this.saving = true;

			await this.$axios.post(`${this.$apiBase}/v1/settings/${this.settings.id}/welcomer`, {
				"join-channel": this.join,
				"join-message": this.joinMessage,
				"join-direct-message": this.joinPrivate,
				"leave-channel": this.leave,
				"leave-message": this.leaveMessage
			}, {
				withCredentials: true,
				credentials: "same-origin"
			});

			await this.fetchGuildData();
			this.changes = {};
			this.saving = false;
			this.updateBar();
		},
		update: async function (field) {
			switch (field) {
				case "join-opt":
					this.join = this.joinOpt ? this.joinOpt.value : null;
					if (this.join !== this.settings.join_message_channel) this.changes[field] = true;
					else this.changes[field] = false;
					break;
				case "join-msg":
					this.joinMessage = this.joinMessage ? this.joinMessage : null;
					if (this.joinMessage !== this.settings.join_message_content) this.changes[field] = true;
					else this.changes[field] = false;
					break;
				case "join-private":
					this.joinPrivate = this.joinPrivate ? this.joinPrivate : null;
					if (this.joinPrivate !== this.settings.join_private_message) this.changes[field] = true;
					else this.changes[field] = false;
					break;
				case "leave-opt":
					this.leave = this.leaveOpt ? this.leaveOpt.value : null;
					if (this.leave !== this.settings.leave_message_channel) this.changes[field] = true;
					else this.changes[field] = false;
					break;
				case "leave-msg":
					this.leaveMessage = this.leaveMessage ? this.leaveMessage : null;
					if (this.leaveMessage !== this.settings.leave_message_content) this.changes[field] = true;
					else this.changes[field] = false;
					break;
				default:
					console.error(`${field} is not a valid settings field.`);
			}
			this.updateBar();
		},
		updateBar: function () {
			if (this.arraify(this.changes).some(c => c === true)) {
				this.confirm = true;
			} else {
				this.confirm = false;
			}
		},
		discard: function () {
			this.init();
			this.changes = {};
			this.updateBar();
		}
	},
	mounted() {
		this.fetchGuildData();
	}
}
</script>